import { type Edge } from '@xyflow/react';
import { stratify, tree, type HierarchyPointNode } from 'd3-hierarchy';

import { type CustomNode } from '../node';

export const DEFAULT_NODE_WIDTH = 420;
const MIN_SPACING = 32;

type LayoutNode = HierarchyPointNode<CustomNode>;

export const makeTreeLayout = (
  nodes: CustomNode[],
  edges: Edge[],
  nodeHeights: Map<string, number>,
): { displayNodes: CustomNode[]; displayEdges: Edge[] } => {
  if (nodes.length === 0 || edges.length === 0) {
    return { displayNodes: [], displayEdges: [] };
  }
  try {
    const hierarchy = stratify<CustomNode>()
      .id((node) => node.id)
      .parentId(
        (node) => edges.find((edge) => edge.target === node.id)?.source,
      );

    const root = hierarchy(nodes);

    const g = tree<CustomNode>()
      .nodeSize([MIN_SPACING, DEFAULT_NODE_WIDTH * 1.3])
      .separation((a, b) => (a.parent === b.parent ? 1 : 1.2));

    const layout = g(root);
    const allNodes = layout.descendants();

    // Group nodes by depth level
    const nodesByDepth = new Map<number, LayoutNode[]>();
    allNodes.forEach((node) => {
      const depthNodes = nodesByDepth.get(node.depth) ?? [];
      depthNodes.push(node);
      nodesByDepth.set(node.depth, depthNodes);
    });

    // Calculate offsets for each depth level independently
    // This prevents overlap of nodes at the SAME depth, not entire subtrees
    const nodeOffsets = new Map<string, number>();

    // Process each depth level
    nodesByDepth.forEach((levelNodes) => {
      // Sort nodes by their Y position (d3's x)
      levelNodes.sort((a, b) => a.x - b.x);

      levelNodes.forEach((node, index) => {
        if (index === 0) {
          nodeOffsets.set(node.data.id, 0);
          return;
        }

        const previousNode = levelNodes[index - 1];
        const previousHeight = nodeHeights.get(previousNode.data.id) ?? 0;
        const previousOffset = nodeOffsets.get(previousNode.data.id) ?? 0;
        const previousY = previousNode.x + previousOffset;

        // Where this node should be: after previous node + its height + spacing
        const desiredY = previousY + previousHeight + MIN_SPACING;

        // Current position from d3
        const currentY = node.x;

        // Calculate offset needed (if any)
        if (desiredY > currentY) {
          nodeOffsets.set(node.data.id, desiredY - currentY);
        } else {
          nodeOffsets.set(node.data.id, 0);
        }
      });
    });

    // Now cascade offsets: children should inherit their parent's offset
    // to maintain the tree structure
    const finalOffsets = new Map<string, number>();

    const calculateFinalOffset = (node: LayoutNode): number => {
      const cached = finalOffsets.get(node.data.id);
      if (cached !== undefined) {
        return cached;
      }

      const ownOffset = nodeOffsets.get(node.data.id) ?? 0;
      const parentOffset = node.parent ? calculateFinalOffset(node.parent) : 0;
      const totalOffset = ownOffset + parentOffset;
      finalOffsets.set(node.data.id, totalOffset);
      return totalOffset;
    };

    allNodes.forEach((node) => {
      calculateFinalOffset(node);
    });

    // Create display nodes with final positions
    const displayNodes: CustomNode[] = allNodes.map((node) => {
      const offset = finalOffsets.get(node.data.id) ?? 0;
      return {
        ...node.data,
        position: {
          x: Math.round(node.y),
          y: Math.round(node.x + offset),
        },
      };
    });

    return {
      displayNodes,
      displayEdges: edges,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in makeTreeLayout:', error);
    return { displayNodes: nodes, displayEdges: edges };
  }
};
